import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import { socket } from 'api';
import { dropCall } from 'store/actions/calls';
import { stopSound } from 'store/actions/sounds';
import { getNodeTitle } from 'services/getNodeTitle';
import { PhoneIcon, CaretIcon } from './icons';
import { Widget, WHead, WIco, WTitle, Chip, Caret, WBody, Dot, Wait, Btn, Empty, CallItem, CallTop, CallName, CallActs } from './widgets-dock.styled';

/** unix-timestamp id → mm:ss waited; null if it doesn't look like a timestamp */
const fmtWait = (id, now) => {
  const started = parseFloat(id);
  if (!started || started < 1e9) {
    return null;
  }
  let s = Math.max(0, Math.floor(now / 1000 - started));
  if (s > 60 * 60 * 6) {
    return null;
  }
  const m = Math.floor(s / 60);
  s = s % 60;
  return m + ':' + String(s).padStart(2, '0');
};

const callTitle = (call) => {
  const node = window.config.nodes ? window.config.nodes[call.source_inner_id] : null;
  if (node) {
    return getNodeTitle(node.type, node.id || call.source_inner_id, node.caption);
  }
  return call.source_inner_id;
};

export const CallsWidget = ({ open, onToggle }) => {
  const { t } = useTranslation();
  const calls = useSelector((store) => store.calls.calls, shallowEqual);
  const isActiveCallExist = useSelector((store) => store.widgets.activeWidgets.indexOf('activeCall') >= 0);
  const dispatch = useDispatch();
  const [now, setNow] = useState(Date.now());

  const list = Object.values(calls || {});
  const waiting = list.filter((c) => c.status !== 'active').length;

  // 1s ticker only while there are calls and the widget is open
  useEffect(() => {
    if (!open || list.length === 0) {
      return undefined;
    }
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open, list.length]);

  const answer = (call) => {
    if (isActiveCallExist || call.status === 'active') {
      return;
    }
    dispatch(stopSound('call'));
    socket.call('call_answer', call);
    window.addActiveCall(call.source_inner_id);
  };

  const hangup = (call) => {
    if (isActiveCallExist) {
      socket.call('call_end', { id: call.id, target: call.source_inner_id });
    } else {
      socket.call('call_reject', { id: call.id, source: call.source_inner_id });
    }
    dispatch(stopSound('call'));
    dispatch(dropCall(call.source_inner_id));
  };

  return (
    <Widget className={waiting > 0 && open === false ? '' : waiting > 0 ? 'alert' : ''}>
      <WHead type="button" className={open ? '' : 'closed'} onClick={onToggle}>
        <WIco>
          <PhoneIcon />
        </WIco>
        <WTitle>{t('dock.Вызовы')}</WTitle>
        {list.length > 0 && <Chip className="blue">{list.length}</Chip>}
        <Caret className={open ? 'open' : ''}>
          <CaretIcon />
        </Caret>
      </WHead>
      {open && (
        <WBody>
          {list.length === 0 && <Empty>{t('dock.Нет активных вызовов')}</Empty>}
          {list.map((call) => {
            const active = call.status === 'active';
            const w = fmtWait(call.id, now);
            return (
              <CallItem key={call.source_inner_id} className={active ? 'active' : ''}>
                <CallTop>
                  <Dot className="green" />
                  <CallName>{callTitle(call)}</CallName>
                  {active ? <Wait>{t('dock.разговор')}</Wait> : w ? <Wait>{w}</Wait> : null}
                </CallTop>
                <CallActs>
                  {!active && (
                    <Btn className="ok" onClick={() => answer(call)} disabled={isActiveCallExist} title={isActiveCallExist ? t('dock.Уже идёт разговор') : t('dock.Ответить')}>
                      {t('dock.Ответить')}
                    </Btn>
                  )}
                  <Btn className="no" onClick={() => hangup(call)}>
                    {active ? t('dock.Завершить разговор') : t('dock.Отклонить')}
                  </Btn>
                </CallActs>
              </CallItem>
            );
          })}
        </WBody>
      )}
    </Widget>
  );
};
